import { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "./stores/authStore";
import { useThemeStore, applyAccentToDom } from "./stores/themeStore";
import { startSessionMaintenance } from "./utils/tokenSession";
import LoginPage from "./pages/LoginPage";
import MailPage from "./pages/MailPage";
import GlobalDialog from "./components/common/GlobalDialog";
import EmailContextMenu from "./components/mail/EmailContextMenu";
import ToastHost from "./components/common/ToastHost";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token);
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  const isDark = useThemeStore((s) => s.isDark);
  const accentColor = useThemeStore((s) => s.accentColor);

  useEffect(() => {
    applyAccentToDom(accentColor, isDark);
  }, [accentColor, isDark]);
  useEffect(() => {
    // Mantener sesión viva en background; renueva al abrir si el token está por expirar.
    startSessionMaintenance();
    const st = useAuthStore.getState();
    if (st.token && st.user?.refreshToken) {
      if (st.user.expiresAt && Date.now() >= st.user.expiresAt - 60_000) {
        void st.refreshAccessToken();
      }
    }
  }, []);
  useEffect(() => {
    // 1. Prevent default browser context menu globally, except in editable text fields if needed
    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const isEditable = target && (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable ||
        target.closest("[contenteditable]")
      );
      // Allow browser native menu only in input fields if user is typing; block everywhere else
      if (!isEditable) {
        e.preventDefault();
      }
    };

    // 2. Block pinch zoom on trackpad (wheel event with ctrlKey) & Ctrl + / Ctrl - key combos
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "+" || e.key === "-" || e.key === "=" || e.key === "0")) {
        e.preventDefault();
      }
    };

    // 3. Block multi-touch pinch zoom on touch screens / iOS Safari
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    const handleGesture = (e: Event) => {
      e.preventDefault();
    };

    window.addEventListener("contextmenu", handleContextMenu);
    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("gesturestart", handleGesture);
    window.addEventListener("gesturechange", handleGesture);
    window.addEventListener("gestureend", handleGesture);

    return () => {
      window.removeEventListener("contextmenu", handleContextMenu);
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("gesturestart", handleGesture);
      window.removeEventListener("gesturechange", handleGesture);
      window.removeEventListener("gestureend", handleGesture);
    };
  }, []);

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MailPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <GlobalDialog />
      <EmailContextMenu />
      <ToastHost />
    </>
  );
}
